import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { DefaultListValues } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';

import Filter, { FilterOptions } from './filter/filter.component';
import useList from './list.controller';
import LoadMoreButton from './load-more-button/load-more-button.component';

const ID = 'list';
export interface ListProps<T extends { id: string }> {
    /**
     * Made mandatory
     * This will make our life a lot easier
     */
    id: string;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>;
    dispatch: ThunkDispatch<any, any, Action>;
    eventListener?: EntityType;
    defaultListValues?: DefaultListValues;
    selectedObjects?: (obj: T[]) => void;

    listOptions?: {
        showCount?: boolean;
        emptyListOptions?: {
            placeholder?: string | ((props: { className?: string }) => React.JSX.Element);
            text?: React.ReactNode;
        };
    };
    rowElement: (props: T, index: number, count: number) => React.ReactNode;
    title?: React.ReactNode;
    emptyState?: () => React.ReactNode;

    filter?: FilterOptions;
    search?: string | null;

    containerClassName?: string;
}

export const List = <T extends { id: string }>(props: ListProps<T>) => {
    const viewProps = useList<T>(props);

    if (viewProps.loading) {
        return (
            <div
                id={ID + '-' + viewProps.listId}
                className='inline-flex w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                <Spinner />
            </div>
        );
    }

    // Keep mounted when search/filters or empty messaging are configured; otherwise hide blank sections.
    if (viewProps.data?.length === 0 && !props.emptyState && !props.filter && !props.listOptions?.emptyListOptions) {
        return null;
    }

    return (
        <div
            id={ID + '-' + viewProps.listId}
            className={props.containerClassName ?? 'flex size-full flex-col gap-3'}>
            {props.title && <p className='font-medium'>{props.title}</p>}

            {props.filter && (
                <Filter
                    dispatch={props.dispatch}
                    filter={props.filter}
                    {...viewProps}
                />
            )}

            {viewProps.data && viewProps.data.length === 0 ? (
                <div className='inline-flex w-full flex-col items-center justify-center gap-3 self-stretch py-4'>
                    {props.emptyState ? (
                        props.emptyState()
                    ) : (
                        <>
                            {props.listOptions?.emptyListOptions?.placeholder && (
                                <>
                                    {typeof props.listOptions.emptyListOptions.placeholder === 'string' ? (
                                        <img
                                            src={props.listOptions.emptyListOptions.placeholder}
                                            alt='Empty list placeholder'
                                        />
                                    ) : (
                                        <props.listOptions.emptyListOptions.placeholder />
                                    )}
                                </>
                            )}
                            <span className='text-secondary py-3 text-center text-sm font-medium'>
                                {props.listOptions?.emptyListOptions?.text ?? 'no data'}
                            </span>
                        </>
                    )}
                </div>
            ) : (
                <>
                    {!(props.listOptions?.showCount === false) && (
                        <span className='text-secondary w-full py-3 text-center text-sm font-medium'>{`total: ${viewProps.totalCount}`}</span>
                    )}
                    <ul className='flex flex-col items-start justify-start gap-2'>
                        {viewProps.data && viewProps.data.map((obj, index) => props.rowElement(obj, index, viewProps.data!.length))}
                        <LoadMoreButton
                            dispatch={props.dispatch}
                            listId={viewProps.listId}
                        />
                    </ul>
                </>
            )}
        </div>
    );
};

export default List;
